import { CreateShiftDTO, ShiftTimeOfDay, ShiftPattern } from '../types';

// 재사용 가능한 시프트 유효성 검사 함수
export const validateShiftData = (data: Partial<CreateShiftDTO>): string | null => {
  // 필수 필드 검증
  if (!data.hospitalId) return '병원 ID가 필요합니다.';
  // 부서 ID는 선택적 필드로 변경
  if (!data.jobType) return '직무 유형을 선택해주세요.';
  if (!data.startDate) return '시작 날짜를 선택해주세요.';
  if (!data.endDate) return '종료 날짜를 선택해주세요.';
  if (!data.timeType) return '시간대를 선택해주세요.';
  if (!data.startTime) return '시작 시간을 선택해주세요.';
  if (!data.endTime) return '종료 시간을 선택해주세요.';
  if (!data.hourlyRate || data.hourlyRate <= 0) return '유효한 시급을 입력해주세요.';
  if (!data.jobDescription || data.jobDescription.trim() === '') 
    return '업무 설명을 입력해주세요.';
  
  // 교육 수준 검증 - 백엔드 요구사항에 맞게 추가
  const validEducationLevels = ["무관", "2년제", "3년제", "4년제"];
  if (!data.educationLevel || !validEducationLevels.includes(data.educationLevel)) {
    return '교육 수준은 무관, 2년제, 3년제, 4년제 중 하나여야 합니다.';
  }
  
  // 커스텀 패턴 유효성 검사  
  if (data.pattern === ShiftPattern.CUSTOM && 
     (!data.customDays || data.customDays.length === 0)) {
    return '커스텀 패턴의 경우 최소 하나 이상의 날짜를 선택해야 합니다.';
  }
  
  // 시간 형식 검증
  try {
    if (data.startTime && !isValidTimeFormat(data.startTime)) {
      return '시작 시간 형식이 올바르지 않습니다. HH:MM 형식이어야 합니다.';
    }
    
    if (data.endTime && !isValidTimeFormat(data.endTime)) {
      return '종료 시간 형식이 올바르지 않습니다. HH:MM 형식이어야 합니다.';
    }
  } catch (e) {
    return '시간 형식이 올바르지 않습니다.';
  }
  
  // 날짜 논리 유효성 검사
  if (data.startDate && data.endDate) {
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '날짜 형식이 올바르지 않습니다.';
    }
    
    if (endDate < startDate) {
      return '종료 날짜는 시작 날짜 이후여야 합니다.';
    }
  }
  
  // 시간대 유효성 검사 (백엔드 로직과 동일하게)
  if (data.startTime && data.timeType) {
    const [startHour] = data.startTime.split(':').map(Number);
    
    switch (data.timeType) {
      case ShiftTimeOfDay.AM:
        if (startHour < 6 || startHour >= 14)
          return '오전 근무는 06:00부터 13:59 사이로 설정해야 합니다.';
        break;
      case ShiftTimeOfDay.PM:
        if (startHour < 14 || startHour >= 22)
          return '오후 근무는 14:00부터 21:59 사이로 설정해야 합니다.';
        break;
      case ShiftTimeOfDay.NOC:
        // NOC는 22:00~23:59 또는 00:00~05:59 허용 (22시부터 익일 6시 전까지)
        if (!(startHour >= 22 || startHour < 6))
          return '야간 근무는 22:00부터 05:59 사이로 설정해야 합니다.';
        break;
    }
  }
  
  // 시작 시간과 종료 시간 비교 (야간 근무 특별 처리)
  if (data.startTime && data.endTime && data.timeType !== ShiftTimeOfDay.NOC) {
    const [startHour, startMinute] = data.startTime.split(':').map(Number);
    const [endHour, endMinute] = data.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    if (startMinutes >= endMinutes) {
      return '시작 시간은 종료 시간보다 빨라야 합니다.';
    }
  }
  
  return null; // 유효성 검사 통과
};

// 유효한 시간 형식인지 확인 (HH:MM)
function isValidTimeFormat(timeStr: string): boolean {
  // HTML time input 값은 형식이 "HH:MM" 또는 "HH:MM:SS"이기 때문에 단순히 존재 여부만 검증
  // 브라우저가 이미 입력 형식을 강제함
  if (!timeStr) return false;
  
  // 기본 정규식 검증 (00:00부터 23:59까지)
  const regex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]))?$/;
  return regex.test(timeStr);
}

// API 에러 메시지 정제 함수
export const getApiErrorMessage = (error: any): string => {
  if (!error) return '알 수 없는 오류가 발생했습니다.';
  
  // errors 필드가 있는 경우 (백엔드에서 구체적인 에러 정보를 반환한 경우)
  if (error.errors) {
    // 객체로 반환된 경우
    if (typeof error.errors === 'object' && !Array.isArray(error.errors)) {
      return Object.values(error.errors).flat().join(', ');
    }
    
    // 문자열 배열로 반환된 경우
    if (Array.isArray(error.errors)) {
      return error.errors.join(', ');
    }
    
    // 문자열로 반환된 경우
    if (typeof error.errors === 'string') {
      return error.errors;
    }
  }
  
  // 특정 에러 코드나 메시지에 대한 사용자 친화적 메시지 매핑
  if (error.statusCode === 400) {
    if (error.message.includes('time')) {
      return '시간 형식이 올바르지 않습니다. 다시 확인해주세요.';
    }
    if (error.message.includes('date')) {
      return '날짜 형식이 올바르지 않습니다. 다시 확인해주세요.';
    }
    if (error.message.includes('pattern')) {
      return '근무 패턴이 올바르지 않습니다. 다시 확인해주세요.';
    }
    if (error.message.includes('required')) {
      return '필수 입력 항목이 누락되었습니다. 다시 확인해주세요.';
    }
  }
  
  // 기본 메시지 반환
  return error.message || '서버 오류가 발생했습니다.';
};

// ISO 형식 확인 유틸리티 함수
export const ensureISOFormat = (dateTimeString: string): string => {
  // 이미 ISO 형식인지 확인
  if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
    return dateTimeString;
  }
  
  // ISO 형식으로 변환 시도
  try {
    return new Date(dateTimeString).toISOString();
  } catch (e) {
    console.error('Date format error:', e);
    return dateTimeString; // 변환 실패 시 원본 반환
  }
}; 